import React, { useEffect, useMemo } from 'react';
import type { CollapseProps } from 'antd';
import { Collapse, Space } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ReactComponent as MapIcon } from '../../../assets/svg/map-icn.svg';
import { ReactComponent as ScanIcon } from '../../../assets/svg/scan-icn.svg';
import { ReactComponent as CarrierIcon } from '../../../assets/svg/carrier-icn.svg';
import ScanCarrierDetails from './ScanCarrierDetails';
import DamageList from './DamageList';
import type { InspectionDamage, VinInspectionItem } from '../inspectionTypes';
import { features } from '../../../config/platform';
import { useCollapseAllPanels } from '../hooks/useCollapseAllPanels';
import { normalizeInspectionViewName } from '../inspectionViewConstants';
import { isDamageDeleted } from '../utils/damageAnnotationUtils';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';

interface InspectionDamagePanelProps {
  inspection: VinInspectionItem;
  viewNames: string[];
  damages: InspectionDamage[];
  onOpenModal?: (imageData?: InspectionMediaData, type?: InspectionMediaType) => void;
  isMobileInspection?: boolean;
  expandAllViews?: boolean;
}

const InspectionDamagePanel: React.FC<InspectionDamagePanelProps> = ({
  inspection,
  viewNames,
  damages,
  onOpenModal,
  isMobileInspection = false,
  expandAllViews = false,
}) => {
  const showScanCarrierDetails = features.isCloudPortal;

  const panelKeys = useMemo(() => {
    const baseKeys = viewNames.map((_, index) => String(index + 1));
    return showScanCarrierDetails ? [...baseKeys, String(baseKeys.length + 1)] : baseKeys;
  }, [viewNames, showScanCarrierDetails]);

  const { activeKeys, setActiveKeys, shouldShowCollapseAll, isPanelActive, togglePanel, toggleAll } = useCollapseAllPanels(
    panelKeys,
    isMobileInspection
  );

  useEffect(() => {
    const additionalKey = String(viewNames.length + 1);

    if (!viewNames.length) {
      setActiveKeys(showScanCarrierDetails ? [additionalKey] : []);
      return;
    }

    if (expandAllViews) {
      setActiveKeys(panelKeys);
      return;
    }

    setActiveKeys(showScanCarrierDetails ? ['1', additionalKey] : ['1']);
  }, [viewNames, panelKeys, expandAllViews, showScanCarrierDetails, setActiveKeys]);

  const renderExtraTabs = (key: string, viewName: string) => (
    <Space size="middle" align="center">
      {viewName === viewNames[0] && (
        <span
          onClick={e => {
            e.stopPropagation();
            toggleAll();
          }}
          className="text-gray-600 cursor-pointer hover:text-gray-800 mr-2 text-sm font-medium">
          {shouldShowCollapseAll ? 'Collapse All' : 'Expand All'}
        </span>
      )}
      <span
        onClick={e => {
          e.stopPropagation();
          togglePanel(key);
        }}
        className="cursor-pointer text-gray-400 hover:text-gray-600">
        {isPanelActive(key) ? <UpOutlined /> : <DownOutlined />}
      </span>
    </Space>
  );

  const items: CollapseProps['items'] = [
    ...viewNames.map((view, index) => {
      const normalizedView = normalizeInspectionViewName(view) || view;
      const filteredDamages = damages.filter(item => {
        if (isDamageDeleted(item)) {
          return false;
        }

        const damageView = normalizeInspectionViewName(item?.viewName);
        return damageView === normalizedView && Boolean(item?.newMetadata || item?.metadata);
      });

      return {
        key: String(index + 1),
        label: `${normalizedView} View`,
        children: (
          <DamageList
            viewName={normalizedView}
            damages={filteredDamages}
            handleItemClick={onOpenModal}
            isMobileInspection={isMobileInspection}
          />
        ),
        extra: renderExtraTabs(String(index + 1), normalizedView),
      };
    }),
    ...(showScanCarrierDetails
      ? [
          {
            key: String(viewNames.length + 1),
            label: (
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div className="flex items-center justify-start gap-2 font-bold">
                  <span>{inspection?.location || 'N/A'}</span>
                  <MapIcon width={20} height={20} />
                </div>
                <div className="flex items-center justify-start gap-2 font-bold">
                  <span>Scan Details</span>
                  <ScanIcon width={20} height={20} />
                </div>
                <div className="flex items-center justify-start gap-2 font-bold">
                  <span>Carrier Details</span>
                  <CarrierIcon width={20} height={20} />
                </div>
              </div>
            ),
            children: <ScanCarrierDetails inspection={inspection} />,
            extra: renderExtraTabs(String(viewNames.length + 1), 'Additional'),
          },
        ]
      : []),
  ];

  return (
    <Collapse
      items={items}
      bordered={false}
      activeKey={activeKeys}
      onChange={keys => setActiveKeys(keys as string[])}
      className="bg-transparent"
      expandIcon={() => null}
    />
  );
};

export default InspectionDamagePanel;
